import sentry from '../../../sentry';
import { useReportAndToastApiError } from '../../../hooks/useReportAndToastApiError';
import {
  Button,
  Box,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  FormControl,
  VStack,
  Link,
  Text,
  Checkbox,
  Divider,
  Collapse,
  useColorModeValue,
  Heading,
  Flex,
  useToast,
} from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useState, useContext, useEffect, useRef } from 'react';
import { HttpContext } from '../../../context/HttpContext';
import { MeContext } from '../../../context/MeContext';
import PassesListContext from '../../../store/client/PassesListContext';
import {
  CustomReactSelect,
  formatOptionLabelFilterPassTemplate,
  formatOptionLabelSelectPassType,
  formatOptionLabelSelectPassStatus,
  formatOptionLabelSelectDeviceType,
  formatOptionLabelFilterGroupTag,
} from '../../common/CustomReactSelect';
import CustomSearchableSelect from '../../common/CustomSearchableSelect';
import { fetchGroupTags } from '../../../services/groupTagService';
import { handleCheckboxFilter } from '../../../utils/handleCheckboxFilter.js';
import FilterButton from '../../common/FilterButton';
import ClearFiltersButton from '../../common/ClearFiltersButton';

function PassesListFilter() {
  const toast = useToast();
  const reportAndToastApiError = useReportAndToastApiError();
  const { authAxios } = useContext(HttpContext);
  const passListCtx = useContext(PassesListContext);
  const [passTemplatesList, setPassTemplatesList] = useState([]);
  const [groupTagsList, setGroupTagsList] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenId, onToggle: onToggleId } = useDisclosure();
  const { isOpen: isOpenExtId, onToggle: onToggleExtId } = useDisclosure();
  const { isOpen: isOpenPassTemplate, onToggle: onTogglePassTemplate } =
    useDisclosure();
  const { isOpen: isOpenGroupTag, onToggle: onToggleGroupTag } =
    useDisclosure();
  const { isOpen: isOpenPassType, onToggle: onTogglePassType } =
    useDisclosure();
  const { isOpen: isOpenDeviceType, onToggle: onToggleDeviceType } =
    useDisclosure();
  const { isOpen: isOpenStatus, onToggle: onToggleStatus } = useDisclosure();
  const [selectedUuid, setSelectedUuid] = useState('');
  const [selectedExternalId, setSelectedExternalId] = useState('');
  const [selectedPassTemplate, setSelectedPassTemplate] = useState('');
  const [selectedGroupTags, setSelectedGroupTags] = useState([]);
  const [selectedPassType, setSelectedPassType] = useState('');
  const [selectedDeviceType, setSelectedDeviceType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const modeBoxBg = useColorModeValue('white', 'secondaryDark');
  const modeTextBg = useColorModeValue('white', 'primaryDark06');
  const modeLabelBg = useColorModeValue('white', 'primaryDark03');
  const modeBorderColor = useColorModeValue('primaryBackground', 'primaryDark');
  const modeLabelColor = useColorModeValue('secondaryDark', 'white');
  const meCtx = useContext(MeContext);
  const { filterEntity } = meCtx.state;

  // Pass type options for Apple

  const passTypes = [
    {
      label: 'Generic pass',
      value: 'generic',
    },
    {
      label: 'Loyalty pass',
      value: 'storeCard',
    },
    {
      label: 'Event ticket',
      value: 'eventTicket',
    },
    {
      label: 'Loyalty stamp card',
      value: 'stampCard',
    },
  ];

  const deviceTypes = [
    {
      label: 'Apple',
      value: 'iphone',
    },
    {
      label: 'Android',
      value: 'android',
    },
  ];

  useEffect(() => {
    const getData = async () => {
      try {
        let response;
        if (filterEntity) {
          response = await authAxios.get(
            `entities/${filterEntity.uuid}/passes/templates?skipImages=true&fields=name`
          );
        } else {
          response = await authAxios.get(
            `passes/templates?skipImages=true&fields=name`
          );
        }
        const data = response?.data?.data;
        data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        let templates = [];
        if (data.length === 0) {
          return;
        }
        data.forEach((item) => {
          templates.push({
            label: item.name,
            value: item,
          });
        });
        setPassTemplatesList(templates);
        // if no filter is set always return to All pass templates default
      } catch (error) {
        reportAndToastApiError(error);
      }
    };

    getData();

    return () => {
      setPassTemplatesList([]);
    };
  }, [authAxios, filterEntity]);

  // fetch all group tags for select group tags dropdown
  useEffect(() => {
    const getGroupTags = async () => {
      try {
        const tags = await fetchGroupTags(authAxios, filterEntity, null);
        setGroupTagsList(tags);
      } catch (error) {
        sentry.sendError(error);
        console.error('Error fetching group tags:', error);
      }
    };

    getGroupTags();
  }, [authAxios, filterEntity]);

  const filterEntityRef = useRef(filterEntity);

  useEffect(() => {
    if (filterEntity !== filterEntityRef.current) {
      filterEntityRef.current = filterEntity;
      onResetAll();
    }
  }, [filterEntity]);

  const { register, handleSubmit, reset, setValue, getValues } = useForm();

  const onSubmit = async (values) => {
    // on submit alter values object and add status from useState
    values.id = selectedUuid ? selectedUuid : '';
    values.extId = selectedExternalId ? selectedExternalId : '';
    values.passTemplate = selectedPassTemplate
      ? selectedPassTemplate.label
      : '';
    values.groupTags = selectedGroupTags
      ? selectedGroupTags.map((tag) => tag.label)
      : [];
    values.passType = selectedPassType ? selectedPassType.value : '';
    values.deviceType = selectedDeviceType ? selectedDeviceType.value : '';
    values.status = selectedStatus ? selectedStatus.status : '';
    values.extIdPrefix = passListCtx?.filterValues?.extIdPrefix || '';
    passListCtx.updateFilterValues(values);

    let countableFilterValues = [];
    Object.entries(values).forEach(([key, value]) => {
      if (!key.startsWith('by') && value !== false && key !== 'extIdPrefix') {
        countableFilterValues = countableFilterValues.concat(value);
      }
    });
    passListCtx.updateTotalActiveFilters(
      countableFilterValues.filter((val) => val.length !== 0).length
    );
    passListCtx.updateNewFiltersSet(true);
    onClose();
  };

  const onResetAll = async () => {
    reset();
    setValue('byId', false);
    setValue('byExternalId', false);
    setValue('byPassTemplate', false);
    setValue('byPassType', false);
    setValue('byDeviceType', false);
    setValue('byStatus', false);
    setValue('byExtIdPrefix', false);
    setValue('byGroupTag', false);

    passListCtx.updateFilterValues({
      id: '',
      extId: '',
      extIdPrefix: '',
      passTemplate: '',
      groupTag: '',
      passType: '',
      deviceType: '',
      status: '',
    });
    passListCtx.updateTotalActiveFilters(0);
    onClose();
    isOpenId && onToggleId();
    isOpenExtId && onToggleExtId();
    isOpenPassTemplate && onTogglePassTemplate();
    isOpenGroupTag && onToggleGroupTag();
    isOpenPassType && onTogglePassType();
    isOpenDeviceType && onToggleDeviceType();
    isOpenStatus && onToggleStatus();
    setSelectedUuid('');
    setSelectedExternalId('');
    setSelectedPassTemplate('');
    setSelectedGroupTags([]);
    setSelectedPassType('');
    setSelectedDeviceType('');
    setSelectedStatus('');
    sessionStorage.setItem('navigatedFromPassPage', 'false');
  };

  const statusList = [
    {
      status: 'expired',
      label: 'Expired',
    },
    {
      status: 'issued',
      label: 'Issued',
    },
    {
      status: 'active',
      label: 'Active',
    },
    // {
    //   status: 'pending',
    //   label: 'Pending',
    // },
    {
      status: 'removed',
      label: 'Removed',
    },
    {
      status: 'deactivated',
      label: 'Deactivated',
    },
  ];

  useEffect(() => {
    const navigatedFromPassPage =
      sessionStorage.getItem('navigatedFromPassPage') === 'true';
    if (!navigatedFromPassPage) return;
    const storedFilters = sessionStorage.getItem('filterValues');
    if (!storedFilters) return;
    const filters = JSON.parse(storedFilters);
    const filterConfig = {
      id: {
        toggleFunction: onToggleId,
        setValueKey: 'id',
        setFlagKey: 'byId',
        setSelectedFunction: setSelectedUuid,
      },
      extId: {
        toggleFunction: onToggleExtId,
        setValueKey: 'extId',
        setFlagKey: 'byExternalId',
        setSelectedFunction: setSelectedExternalId,
      },
      passTemplate: {
        toggleFunction: onTogglePassTemplate,
        setValueKey: 'passTemplate',
        setFlagKey: 'byPassTemplate',
        setSelectedFunction: setSelectedPassTemplate,
        adaptValue: (value) => ({ name: value }),
      },
      groupTag: {
        toggleFunction: onToggleGroupTag,
        setValueKey: 'groupTag',
        setFlagKey: 'byGroupTag',
        setSelectedFunction: setSelectedGroupTags,
        adaptValue: (value) => ({ label: value }),
      },
      passType: {
        toggleFunction: onTogglePassType,
        setValueKey: 'passType',
        setFlagKey: 'byPassType',
        setSelectedFunction: setSelectedPassType,
      },
      deviceType: {
        toggleFunction: onToggleDeviceType,
        setValueKey: 'deviceType',
        setFlagKey: 'byDeviceType',
        setSelectedFunction: setSelectedDeviceType,
      },
      status: {
        toggleFunction: onToggleStatus,
        setValueKey: 'status',
        setFlagKey: 'byStatus',
        setSelectedFunction: setSelectedStatus,
        adaptValue: (value) => ({ status: value }),
      },
    };
    Object.entries(filters).forEach(([key, value]) => {
      const config = filterConfig[key];
      if (config && value) {
        handleCheckboxFilter({
          getValues,
          reset,
          setValue,
          toggleFunction: config.toggleFunction,
          fieldToClear: config.setValueKey,
          clearCallback: () => {
            setValue(config.setValueKey, value || '');
            setValue(config.setFlagKey, true);
            config.setSelectedFunction(
              config.adaptValue ? config.adaptValue(value) : value
            );
          },
        });
      }
    });
  }, [setValue]);

  return (
    <Box>
      <Flex gap={{ base: '10px', sm: '0px' }}>
        {passListCtx.totalActiveFilters > 0 && (
          <ClearFiltersButton onResetAll={onResetAll} />
        )}
        <FilterButton
          onOpen={onOpen}
          totalActiveFilters={passListCtx.totalActiveFilters}
        />
      </Flex>

      <Drawer onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <form onSubmit={handleSubmit(onSubmit)}>
          <DrawerContent bg={modeBoxBg}>
            <DrawerCloseButton />
            <DrawerHeader
              borderBottomWidth="1px"
              borderBottomColor={modeBorderColor}
              pt={{ base: '16px', lg: '56px' }}
              bg={modeTextBg}
            >
              <Heading>Filter Passes</Heading>
            </DrawerHeader>
            <DrawerBody p="0" bg={modeLabelBg}>
              <VStack spacing={0}>
                {/* by pass template */}
                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byPassTemplate"
                    {...register('byPassTemplate')}
                    onChange={() =>
                      handleCheckboxFilter({
                        getValues,
                        reset,
                        setValue,
                        toggleFunction: onTogglePassTemplate,
                        fieldToClear: 'passTemplate',
                        clearCallback: () => {
                          setSelectedPassTemplate('');
                        },
                      })
                    }
                  >
                    <Text color={modeLabelColor}>By pass template</Text>
                  </Checkbox>
                  <Collapse in={isOpenPassTemplate} animateOpacity>
                    <CustomReactSelect
                      options={passTemplatesList}
                      placeholder="Select pass template"
                      formatOptionLabel={formatOptionLabelFilterPassTemplate}
                      onChange={(passTemplate) =>
                        setSelectedPassTemplate({
                          label: passTemplate.label,
                          value: passTemplate.value,
                        })
                      }
                      value={selectedPassTemplate}
                      id="passTemplateFilter"
                      passTemplate
                    />
                  </Collapse>
                </FormControl>

                <Box py="12px" w="full">
                  <Divider borderColor={modeBorderColor} />
                </Box>

                {/* by pass type */}
                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byPassType"
                    {...register('byPassType')}
                    onChange={() =>
                      handleCheckboxFilter({
                        getValues,
                        reset,
                        setValue,
                        toggleFunction: onTogglePassType,
                        fieldToClear: 'passType',
                        clearCallback: () => {
                          setSelectedPassType('');
                        },
                      })
                    }
                  >
                    <Text color={modeLabelColor}>By pass type</Text>
                  </Checkbox>
                  <Collapse in={isOpenPassType} animateOpacity>
                    <CustomReactSelect
                      options={passTypes}
                      formatOptionLabel={formatOptionLabelSelectPassType}
                      placeholder="Select pass type"
                      onChange={(passType) => setSelectedPassType(passType)}
                      value={selectedPassType}
                      id="passTypeFilter"
                      passType
                    />
                  </Collapse>
                </FormControl>

                <Box py="12px" w="full">
                  <Divider borderColor={modeBorderColor} />
                </Box>
                {/* by device type */}
                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byPassType"
                    {...register('byDeviceType')}
                    onChange={() =>
                      handleCheckboxFilter({
                        getValues,
                        reset,
                        setValue,
                        toggleFunction: onToggleDeviceType,
                        fieldToClear: 'deviceType',
                        clearCallback: () => {
                          setSelectedDeviceType('');
                        },
                      })
                    }
                  >
                    <Text color={modeLabelColor}>By device type</Text>
                  </Checkbox>
                  <Collapse in={isOpenDeviceType} animateOpacity>
                    <CustomReactSelect
                      options={deviceTypes}
                      formatOptionLabel={formatOptionLabelSelectDeviceType}
                      placeholder="Select device type"
                      onChange={(deviceType) =>
                        setSelectedDeviceType(deviceType)
                      }
                      value={selectedDeviceType}
                      id="deviceTypeFilter"
                      deviceType
                    />
                  </Collapse>
                </FormControl>

                <Box py="12px" w="full">
                  <Divider borderColor={modeBorderColor} />
                </Box>

                {/* by status */}
                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byStatus"
                    {...register('byStatus')}
                    onChange={() =>
                      handleCheckboxFilter({
                        getValues,
                        reset,
                        setValue,
                        toggleFunction: onToggleStatus,
                        fieldToClear: 'status',
                        clearCallback: () => {
                          setSelectedStatus('');
                        },
                      })
                    }
                  >
                    <Text color={modeLabelColor}>By status</Text>
                  </Checkbox>
                  <Collapse in={isOpenStatus} animateOpacity>
                    <CustomReactSelect
                      options={statusList}
                      placeholder="Select status"
                      formatOptionLabel={formatOptionLabelSelectPassStatus}
                      onChange={(status) => setSelectedStatus(status)}
                      value={selectedStatus}
                      id="passStatus"
                      passStatus
                    />
                  </Collapse>
                </FormControl>

                <Box py="12px" w="full">
                  <Divider borderColor={modeBorderColor} />
                </Box>

                {/* by group tag */}
                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byGroupTag"
                    {...register('byGroupTag')}
                    onChange={() =>
                      handleCheckboxFilter({
                        getValues,
                        reset,
                        setValue,
                        toggleFunction: onToggleGroupTag,
                        fieldToClear: 'groupTag',
                        clearCallback: () => {
                          setSelectedGroupTags([]);
                        },
                      })
                    }
                  >
                    <Text color={modeLabelColor}>By group tag</Text>
                  </Checkbox>
                  <Collapse in={isOpenGroupTag} animateOpacity>
                    <CustomSearchableSelect
                      options={groupTagsList}
                      placeholder="Select group tag"
                      formatOptionLabel={formatOptionLabelFilterGroupTag}
                      onChange={(groupTags) => {
                        setSelectedGroupTags(groupTags);
                      }}
                      loadOptions={async (searchTerm) => {
                        try {
                          const groupTagsList = await fetchGroupTags(
                            authAxios,
                            filterEntity,
                            searchTerm
                          );
                          return groupTagsList;
                        } catch (error) {
                          console.error('Error loading group tags:', error);
                          return [];
                        }
                      }}
                      value={selectedGroupTags}
                      isMulti
                    />
                  </Collapse>
                </FormControl>

                <Box py="12px" w="full">
                  <Divider borderColor={modeBorderColor} />
                </Box>
              </VStack>
            </DrawerBody>
            <DrawerFooter
              borderTopWidth="1px"
              borderTopColor={modeBorderColor}
              bg={modeTextBg}
            >
              <Link
                color={useColorModeValue('secondaryDark08', '#DDDDDD')}
                fontSize="14px"
                onClick={onResetAll}
                mr="30px"
              >
                <Text textStyle="headingFamilyMedium">Reset all</Text>
              </Link>
              <Button
                type="submit"
                alt="Apply filters"
                size="sm"
                px="30px"
                alignSelf="flex-start"
              >
                Apply filters
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </form>
      </Drawer>
    </Box>
  );
}

export default PassesListFilter;
